import React from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Box,
  Typography,
  TablePagination,
  Tooltip,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonIcon from '@mui/icons-material/Person';
import AgentOrderMobileCards from '../../components/agent/AgentOrderMobileCards';

const STATUS_STYLES = {
  Available:  { bgcolor: '#dbeafe', color: '#1d4ed8' },
  Assigned:   { bgcolor: '#fff3cd', color: '#92400e' },
  Completed:  { bgcolor: '#dcfce7', color: '#166534' },
  Delivered:  { bgcolor: '#dcfce7', color: '#166534' },
  DELIVERED:  { bgcolor: '#dcfce7', color: '#166534' },
  Pending:    { bgcolor: '#f3e8ff', color: '#6b21a8' },
  Cancelled:  { bgcolor: '#fee2e2', color: '#991b1b' },
  // delivery tracking keys (labels shown verbatim)
  NONE:             { bgcolor: '#f1f5f9', color: '#64748b' },
  ORDER_PLACED:     { bgcolor: '#e0f2fe', color: '#0369a1' },
  VENDOR_ACCEPTED:  { bgcolor: '#fef3c7', color: '#b45309' },
  ACCEPTED:         { bgcolor: '#fef3c7', color: '#b45309' },
  MANUFACTURING:    { bgcolor: '#ede9fe', color: '#6d28d9' },
  VENDOR_PREPARING: { bgcolor: '#ede9fe', color: '#6d28d9' },
  READY_FOR_PICKUP: { bgcolor: '#bfdbfe', color: '#1e40af' },
  PICKED_UP:        { bgcolor: '#bfdbfe', color: '#1e40af' },
  IN_TRANSIT:       { bgcolor: '#bfdbfe', color: '#1e40af' },
  OUT_FOR_DELIVERY: { bgcolor: '#bfdbfe', color: '#1e40af' },
  DELIVERED:        { bgcolor: '#dcfce7', color: '#166534' },
  CANCELLED:        { bgcolor: '#fee2e2', color: '#991b1b' },
  REJECTED:         { bgcolor: '#fee2e2', color: '#991b1b' },
  VENDOR_REJECTED:  { bgcolor: '#fee2e2', color: '#991b1b' },
};

function ItemsSummary({ items = [] }) {
  if (!items.length) return <Typography variant="body2" sx={{ color: '#94a3b8' }}>—</Typography>;

  const display = items.slice(0, 2);
  const extra = items.length - 2;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {display.map((item, i) => (
        <Typography key={i} variant="body2" sx={{ color: '#1e293b', lineHeight: 1.4 }}>
          <Box component="span" sx={{ fontWeight: 600 }}>{item.qty}×</Box>{' '}
          <Tooltip title={item.title} placement="top" arrow>
            <Box
              component="span"
              sx={{
                maxWidth: '90%',
                display: 'inline-block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                verticalAlign: 'bottom',
                color: '#334155',
              }}
            >
              {item.title}
            </Box>
          </Tooltip>
        </Typography>
      ))}
      {extra > 0 && (
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>+{extra} more</Typography>
      )}
    </Box>
  );
}

export default function AgentRecentOrdersTable({ orders = [], onView }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = React.useState('');

  const sorted = [...orders].sort((a, b) => {
    const dateA = a.rawAcceptedDate ? new Date(a.rawAcceptedDate) : new Date(0);
    const dateB = b.rawAcceptedDate ? new Date(b.rawAcceptedDate) : new Date(0);
    return dateB - dateA;
  });

  const filtered = sorted.filter((o) => {
    if (!search) return true;
    const s = search.toLowerCase();
    const itemsText = (o.items || []).map((i) => i.title).join(' ').toLowerCase();
    return (
      String(o.id || '').toLowerCase().includes(s) ||
      String(o.status || '').toLowerCase().includes(s) ||
      String(o.vendor || '').toLowerCase().includes(s) ||
      String(o.customer || '').toLowerCase().includes(s) ||
      String(o.deliveryAddress || '').toLowerCase().includes(s) ||
      String(o.vendorAddress || '').toLowerCase().includes(s) ||
      itemsText.includes(s)
    );
  });

  const COLUMNS = [
    { label: 'Items',           width: '16%' },
    { label: 'Pickup (Vendor)', width: '23%' },
    { label: 'Delivery',        width: '23%' },
    { label: 'Commission',      width: '8%' },
    { label: 'Status',          width: '17%' },
    { label: 'Accepted',        width: '8%' },
    { label: 'Action',          width: '5%'  },
  ];

  return (
    <Box sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 12px rgba(15,23,42,0.07)', mt: 2 }}>
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1,
          minHeight: 52,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#fff' }}>
          Recent Orders
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            bgcolor: 'rgba(255,255,255,0.10)',
            borderRadius: '8px',
            px: 1.25,
            py: 0.75,
            width: { xs: '100%', sm: 380 },
            border: '1px solid rgba(255,255,255,0.15)',
            '&:focus-within': {
              border: '1px solid rgba(255,255,255,0.45)',
              bgcolor: 'rgba(255,255,255,0.14)',
            },
          }}
        >
          <SearchIcon sx={{ fontSize: 17, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by order #, vendor, product, customer, or address…"
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#fff',
              fontSize: '0.82rem',
              width: '100%',
              caretColor: '#fff',
            }}
          />
        </Box>
      </Box>

      {/* Mobile card layout */}
      {isMobile && (
        <Box sx={{ bgcolor: '#f8fafc', px: 1.5, pb: 1 }}>
          <AgentOrderMobileCards orders={filtered} onView={onView} />
        </Box>
      )}

      {/* Desktop / Tablet table */}
      {!isMobile && (
      <Box sx={{ bgcolor: '#fff', overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.label}
                  sx={{
                    width: col.width,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    py: 1.25,
                    borderBottom: '2px solid #e2e8f0',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((o) => {
              const statusStyle = STATUS_STYLES[o.statusKey] || STATUS_STYLES[o.status] || { bgcolor: '#f1f5f9', color: '#475569' };
              return (
                <TableRow
                  key={o.id}
                  sx={{
                    '&:hover': { bgcolor: '#f8faff' },
                    '& td': { py: 1.25, borderBottom: '1px solid #f1f5f9' },
                  }}
                >
                  {/* Items */}
                  <TableCell sx={{ width: '16%' }}>
                    <ItemsSummary items={o.items} />
                  </TableCell>

                  {/* Pickup / Vendor */}
                  <TableCell sx={{ width: '23%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <StorefrontIcon sx={{ fontSize: 14, color: '#7c3aed', flexShrink: 0, mt: '2px' }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Tooltip title={o.vendor || '—'} placement="top" arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#334155',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {o.vendor || '—'}
                          </Typography>
                        </Tooltip>
                        {o.vendorAddress && (
                          <Tooltip title={o.vendorAddress} placement="top" arrow>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#94a3b8',
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {o.vendorAddress}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Delivery address */}
                  <TableCell sx={{ width: '23%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 14, color: '#1976d2', flexShrink: 0, mt: '2px' }} />
                      <Box sx={{ minWidth: 0 }}>
                        {o.customer && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#334155',
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {o.customer}
                          </Typography>
                        )}
                        <Tooltip title={o.deliveryAddress || '—'} placement="top" arrow>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#94a3b8',
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {o.deliveryAddress || '—'}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Commission */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#16a34a', whiteSpace: 'nowrap' }}>
                      {o.commission}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      of {o.value}
                    </Typography>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Chip
                      label={o.status}
                      size="small"
                      sx={{
                        ...statusStyle,
                        fontWeight: 600,
                        fontSize: '0.68rem',
                        border: 'none',
                        height: 'auto',
                        minHeight: 20,
                        whiteSpace: 'normal',
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                          overflow: 'visible',
                          textOverflow: 'clip',
                          py: '3px',
                        },
                      }}
                    />
                  </TableCell>

                  {/* Accepted */}
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>
                      {o.acceptedDate || '—'}
                    </Typography>
                  </TableCell>

                  {/* Action */}
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => onView && onView(o)}
                      sx={{
                        color: '#1976d2',
                        bgcolor: 'rgba(25,118,210,0.08)',
                        borderRadius: 1.5,
                        '&:hover': { bgcolor: 'rgba(25,118,210,0.16)' },
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 5, color: '#94a3b8' }}>
                  No orders found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
          sx={{ borderTop: '1px solid #f1f5f9', fontSize: '0.8rem' }}
        />
      </Box>
      )}
    </Box>
  );
}
